import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ExecuteSSISPackageActivity } from 'app/model/pipeline/ssis.activity';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { merge } from 'lodash';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { ExecuteSSISPackageChildPackageConfig, ExecuteSSISPackageParameterConfig, stringIsNullOrEmpty } from 'shell-models-lib';
import { Owned } from 'app/model/base/named';
import { ManagedIntegrationRuntime } from 'app/model/integrationRuntime/managed.integrationRuntime';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { akvToResource, akvToModel, isAKVObject, passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from '../linkedservices/azure-key-vault.util';
import { SsisInlinePackageEvent, inlinePackageEventType } from 'app/logger/logger.ssisinlinepackage-event';
import { AzureSqlMILinkedService } from 'app/model/linkedservice/azure-sql-mi.linkedservice';
import { linkedServiceReferenceToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { SSISPackageActivityAdapter } from './ssis-activity.adapter.base';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ExecuteSSISPackageActivity),
    disabled: __IS_FABRIC_DPE__,
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            packageLocation: '*',
            runtime: '*',
            loggingLevel: '*',
            executionCredential: '*',
            connectVia: '*',
            environmentPath: '*',
            projectParameters: '*',
            packageParameters: '*',
            projectConnectionManagers: '*',
            packageConnectionManagers: '*',
            propertyOverrides: '*',
            logLocation: '*'
        }
    }
})
export class ExecuteSSISPackageActivityAdapter extends SSISPackageActivityAdapter<ExecuteSSISPackageActivity, Model.ExecuteSSISPackageActivity> {

    private static readonly componentName = 'ExecuteSSISPackageActivityAdapter';

    public model(): IModel {
        return Metamodel.modelFor(ExecuteSSISPackageActivity);
    }

    protected async _toResource(activity: ExecuteSSISPackageActivity): Promise<Model.ExecuteSSISPackageActivity> {
        const modelActivity: Model.ExecuteSSISPackageActivity =
            super._getCommonActivityProperties(activity, 'ExecuteSSISPackage') as Model.ExecuteSSISPackageActivity;

        // Build package path and environment path
        let packagePath: string;
        let environmentPath: string;
        if (activity.configurationMode === ExecuteSSISPackageActivity.CONFIGURATION_MODE_AUTO) {
            if (activity.ssisFolder) {
                packagePath = activity.ssisFolder;
                if (activity.ssisProject) {
                    packagePath += '/' + activity.ssisProject;
                    if (activity.ssisPackage) {
                        packagePath += '/' + activity.ssisPackage;
                    }
                }
            }
            environmentPath = activity.ssisEnvironment;
        } else if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_FILE || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER) {
            packagePath = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.FILE_PACKAGE_PATH);
        } else if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PROJECT_FILE) {
            const projectPath = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PROJECT_PATH);
            const packageName = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_NAME);
            packagePath = !projectPath && !packageName ? '' : (projectPath ? projectPath : '') + ':' + (packageName ? packageName : '');
        } else if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
            packagePath = !stringIsNullOrEmpty(activity.packageStoreName) ? activity.packageStoreName + '/' + activity.packageStorePath : activity.packageStorePath;
        } else {
            packagePath = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_PATH);
            environmentPath = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.ENVIRONMENT_PATH);
        }

        modelActivity.typeProperties = {
            packageLocation: this.getPackageLocation(activity, packagePath),
            runtime: ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.RUNTIME),
            environmentPath,
            connectVia: activity.ssisIr ? this.toIntegrationRuntimeReferenceModel(activity.ssisIr) : undefined,
            propertyOverrides: this.toPropertyOverrideObject(activity.propertyOverrides),
            executionCredential: activity.useWinauth ? this.getExecutionCredential(activity) : undefined
        };

        const logPath = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.LOG_PATH);
        if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB || logPath) {
            modelActivity.typeProperties.loggingLevel = this.getLoggingLevel(activity);
        }

        if (activity.ssisIr) {
            modelActivity.typeProperties.projectParameters = this.toActivityParameterObject(activity, ExecuteSSISPackageParameterConfig.SCOPE_PROJECT);
            modelActivity.typeProperties.packageParameters = this.toActivityParameterObject(activity, ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE);

            modelActivity.typeProperties.projectConnectionManagers = this.toActivityConnectionManagerObject(activity, ExecuteSSISPackageParameterConfig.SCOPE_PROJECT);
            modelActivity.typeProperties.packageConnectionManagers = this.toActivityConnectionManagerObject(activity, ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE);
        }

        if (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB && logPath) {
            modelActivity.typeProperties.logLocation = this.getLogLocation(activity);
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            merge(modelActivity, parsed);
        }
        return modelActivity;
    }

    protected async _toModel(modelActivity: Model.ExecuteSSISPackageActivity): Promise<ExecuteSSISPackageActivity> {
        const activity: ExecuteSSISPackageActivity =
            ExecuteSSISPackageActivity.MODEL.create<ExecuteSSISPackageActivity>(modelActivity.name, modelActivity.description);

        this._updatedCommonProperties(modelActivity, activity);

        if (modelActivity.typeProperties) {
            if (modelActivity.typeProperties.packageLocation) {
                // Set package path
                if (!modelActivity.typeProperties.packageLocation.type ||
                    modelActivity.typeProperties.packageLocation.type === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                    activity.packageLocation = ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB;
                    ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_PATH, modelActivity.typeProperties.packageLocation.packagePath);
                    if (!ExecuteSSISPackageActivity.PACKAGE_PATH.templateValue(activity) && activity.packagePath) {
                        const pathSegments = activity.packagePath.split('\/');
                        activity.ssisFolder = pathSegments[0];
                        activity.ssisProject = pathSegments.length > 1 ? pathSegments[1] : null;
                        activity.ssisPackage = pathSegments.length > 2 ? pathSegments[2] : null;
                    }
                } else {
                    ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.FILE_PACKAGE_PATH, modelActivity.typeProperties.packageLocation.packagePath);
                    if (modelActivity.typeProperties.packageLocation.type === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE) {
                        activity.packageLocation = ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE;
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.INLINE_PACKAGE_NAME, modelActivity.typeProperties.packageLocation.typeProperties.packageName);
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_LAST_MODIFIED_DATE, modelActivity.typeProperties.packageLocation.typeProperties.packageLastModifiedDate);
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_CONTENT, modelActivity.typeProperties.packageLocation.typeProperties.packageContent);
                        activity.childPackages = this.toChildPackageClass(modelActivity.typeProperties.packageLocation.typeProperties.childPackages, activity);
                        activity.useChildPackage = activity.childPackages.length > 0;
                    } else if (modelActivity.typeProperties.packageLocation.type === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER) {
                        activity.packageLocation = modelActivity.typeProperties.packageLocation.type;
                        activity.sqlServerLinkedService = ReferenceAdapter.toProxyModel<AzureSqlMILinkedService>(Metamodel.modelFor(AzureSqlMILinkedService),
                            modelActivity.typeProperties.packageLocation.typeProperties.sqlLinkedService, this.context);
                    } else if (modelActivity.typeProperties.packageLocation.type === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
                        activity.packageLocation = modelActivity.typeProperties.packageLocation.type;
                        const index = modelActivity.typeProperties.packageLocation.packagePath.replace(/\\/g, '/').indexOf('/');
                        if (index !== -1) {
                            activity.packageStoreName = modelActivity.typeProperties.packageLocation.packagePath.substring(0, index);
                            activity.packageStorePath = modelActivity.typeProperties.packageLocation.packagePath.substring(index + 1);
                        }
                    } else if (!ExecuteSSISPackageActivity.FILE_PACKAGE_PATH.templateValue(activity) && activity.filePackagePath &&
                        !modelActivity.typeProperties.packageLocation.typeProperties.configurationPath &&
                        activity.filePackagePath.indexOf(':') >= 0) {
                        activity.packageLocation = ExecuteSSISPackageActivity.PACKAGE_LOCATION_PROJECT_FILE;
                        const pathSegments = modelActivity.typeProperties.packageLocation.packagePath.split(':');
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PROJECT_PATH, pathSegments[0]);
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_NAME, pathSegments.length > 1 ? pathSegments[1] : null);
                        activity.filePackagePath = undefined;
                    } else {
                        activity.packageLocation = ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_FILE;
                    }
                }
                if (modelActivity.typeProperties.packageLocation.typeProperties) {
                    // Set package password
                    const packagePassword = modelActivity.typeProperties.packageLocation.typeProperties.packagePassword;
                    if (packagePassword) {
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_PASSWORD,
                            isExpressionObject(packagePassword) ? packagePassword.value : secretBaseToPasswordOrAKV(packagePassword, true, this.context));
                    }
                    // Set configuration file
                    if (modelActivity.typeProperties.packageLocation.typeProperties.configurationPath) {
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.CONFIGURATION_FILE,
                            modelActivity.typeProperties.packageLocation.typeProperties.configurationPath);
                    }

                    // Set package access credential
                    const accessCredential = modelActivity.typeProperties.packageLocation.typeProperties.accessCredential;
                    if (accessCredential) {
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_ACCESS_DOMAIN,
                            isAKVObject(accessCredential.domain) ? akvToModel(accessCredential.domain, this.context) : accessCredential.domain);
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_ACCESS_USERNAME,
                            isAKVObject(accessCredential.userName) ? akvToModel(accessCredential.userName, this.context) : accessCredential.userName);
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.PACKAGE_ACCESS_PASSWORD,
                            isExpressionObject(accessCredential.password) ? accessCredential.password.value :
                                secretBaseToPasswordOrAKV(accessCredential.password, true, this.context));
                    }

                    // Set configuration access credential
                    activity.useConfigAccessCredentials = false;
                    const configAccessCredential = modelActivity.typeProperties.packageLocation.typeProperties.configurationAccessCredential;
                    if (configAccessCredential) {
                        activity.useConfigAccessCredentials = true;
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.CONFIG_ACCESS_DOMAIN,
                            isAKVObject(configAccessCredential.domain) ? akvToModel(configAccessCredential.domain, this.context) : configAccessCredential.domain);
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.CONFIG_ACCESS_USERNAME,
                            isAKVObject(configAccessCredential.userName) ? akvToModel(configAccessCredential.userName, this.context) : configAccessCredential.userName);
                        ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.CONFIG_ACCESS_PASSWORD,
                            isAKVObject(configAccessCredential.password) ? akvToModel(configAccessCredential.password, this.context) : configAccessCredential.password);
                    }
                }
            }

            // Set runtime
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.RUNTIME, modelActivity.typeProperties.runtime);

            // Set logging level
            this.setLoggingLevel(activity, modelActivity.typeProperties.loggingLevel);

            // Set logging location
            if (modelActivity.typeProperties.logLocation && activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
                ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.LOG_PATH, modelActivity.typeProperties.logLocation.logPath);
                const accessCredential = modelActivity.typeProperties.logLocation.typeProperties.accessCredential;
                ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.LOG_ACCESS_DOMAIN,
                    isAKVObject(accessCredential.domain) ? akvToModel(accessCredential.domain, this.context) : accessCredential.domain);
                ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.LOG_ACCESS_USERNAME,
                    isAKVObject(accessCredential.userName) ? akvToModel(accessCredential.userName, this.context) : accessCredential.userName);
                ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.LOG_ACCESS_PASSWORD,
                    isExpressionObject(accessCredential.password) ? accessCredential.password.value :
                        secretBaseToPasswordOrAKV(accessCredential.password, true, this.context));
            }

            // set execution credential
            this.setExecutionCredential(activity, modelActivity.typeProperties.executionCredential);

            // Set environment path
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.ENVIRONMENT_PATH, modelActivity.typeProperties.environmentPath);
            if (!ExecuteSSISPackageActivity.ENVIRONMENT_PATH.templateValue(activity)) {
                activity.ssisEnvironment = activity.environmentPath;
            }

            // Set ir
            if (modelActivity.typeProperties.connectVia) {
                activity.ssisIr =
                    ReferenceAdapter.toProxyModel<ManagedIntegrationRuntime>(Metamodel.modelFor(ManagedIntegrationRuntime), modelActivity.typeProperties.connectVia, this.context);
            }

            // Set parameters
            const projectParameters = this.toSsisParameterClass(modelActivity.typeProperties.projectParameters, activity, ExecuteSSISPackageParameterConfig.SCOPE_PROJECT, this.context);
            const packageParameters = this.toSsisParameterClass(modelActivity.typeProperties.packageParameters, activity, ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE, this.context);
            activity.parameters = projectParameters.concat(packageParameters);

            const projectConnectionManagers =
                this.toActivityConnectionManagerClass(modelActivity.typeProperties.projectConnectionManagers, activity, ExecuteSSISPackageParameterConfig.SCOPE_PROJECT, this.context);
            const packageConnectionManagers =
                this.toActivityConnectionManagerClass(modelActivity.typeProperties.packageConnectionManagers, activity, ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE, this.context);
            activity.connectionManagerParameters = projectConnectionManagers.concat(packageConnectionManagers);

            activity.propertyOverrides = this.toPropertyOverrideClass(modelActivity.typeProperties.propertyOverrides, activity);

            // Set mode
            if (activity.packageLocation !== ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB
                || ExecuteSSISPackageActivity.PACKAGE_PATH.templateValue(activity) || ExecuteSSISPackageActivity.ENVIRONMENT_PATH.templateValue(activity)) {
                activity.configurationMode = ExecuteSSISPackageActivity.CONFIGURATION_MODE_MANUAL;
            }
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, ExecuteSSISPackageActivity.MODEL));
        return activity;
    }

    private toIntegrationRuntimeReferenceModel(integrationRuntime: IntegrationRuntime): Model.IntegrationRuntimeReference {
        return ReferenceAdapter.toResource(integrationRuntime, this.context) as Model.IntegrationRuntimeReference;
    }

    private getPackageLocation(activity: ExecuteSSISPackageActivity, path: string): Model.SSISPackageLocation {
        if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB) {
            return this.getSSISDBPackageLocation(path);
        } else if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_FILE || activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PROJECT_FILE) {
            return this.getFilePackageLocation(activity, path);
        } else if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE) {
            return this.getInlinePackageLocation(activity);
        } else if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER) {
            return this.getSqlServerPackageLocation(activity, path);
        } else if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE) {
            return this.getPackageStorePackageLocation(activity, path);
        } else {
            this._logError(ExecuteSSISPackageActivityAdapter.componentName + ' Unknow package location type: ' + activity.packageLocation + '. Use SSISDB by default.');
            return this.getSSISDBPackageLocation(path);
        }
    }

    private getSSISDBPackageLocation(path: string): Model.SSISPackageLocation {
        return {
            packagePath: path,
            type: ExecuteSSISPackageActivity.PACKAGE_LOCATION_SSISDB
        };
    }

    private getFilePackageLocation(activity: ExecuteSSISPackageActivity, path: string): Model.SSISPackageLocation {
        const packagePassword = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_PASSWORD);
        const accessDomain = ValueAdapter.toResource(activity,
            activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_DOMAIN : ExecuteSSISPackageActivity.PACKAGE_ACCESS_DOMAIN);
        const accessUserName = ValueAdapter.toResource(activity,
            activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_USERNAME : ExecuteSSISPackageActivity.PACKAGE_ACCESS_USERNAME);
        const accessPassword = ValueAdapter.toResource(activity,
            activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_PASSWORD : ExecuteSSISPackageActivity.PACKAGE_ACCESS_PASSWORD);
        const packageAccessCredential: Model.SSISAccessCredential = {
            domain: (accessDomain && accessDomain instanceof AzureKeyVault) ? akvToResource(accessDomain, this.context) : accessDomain,
            userName: (accessUserName && accessUserName instanceof AzureKeyVault) ? akvToResource(accessUserName, this.context) : accessUserName,
            password: accessPassword ? (isExpressionObject(accessPassword) ? accessPassword : passwordOrAKVtoSecretBase(accessPassword, this.context)) : undefined
        };
        const configurationPath = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.CONFIGURATION_FILE);
        const locTypeProperties: Model.SSISPackageLocationTypeProperties = {
            packagePassword: packagePassword ? (isExpressionObject(packagePassword) ? packagePassword : passwordOrAKVtoSecretBase(packagePassword, this.context)) : undefined,
            accessCredential: packageAccessCredential,
            configurationPath: configurationPath ? configurationPath : undefined
        };
        return {
            packagePath: path,
            type: ExecuteSSISPackageActivity.PACKAGE_LOCATION_FILE,
            typeProperties: locTypeProperties
        };
    }

    private getInlinePackageLocation(activity: ExecuteSSISPackageActivity): Model.SSISPackageLocation {
        const packagePassword = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_PASSWORD);
        const packageName = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.INLINE_PACKAGE_NAME);
        const packageContent = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_CONTENT);
        const packageLastModifiedDate = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_LAST_MODIFIED_DATE);
        const childPackages = activity.useChildPackage ? this.toChildPackageObject(activity.childPackages) : undefined;
        const locTypeProperties: Model.SSISPackageLocationTypeProperties = {
            packagePassword: packagePassword ? (isExpressionObject(packagePassword) ? packagePassword : passwordOrAKVtoSecretBase(packagePassword, this.context)) : undefined,
            packageName,
            packageContent,
            packageLastModifiedDate,
            childPackages
        };
        const mainPackageSize = packageContent ? isExpressionObject(packageContent) ? 'expression' : Buffer.from(packageContent).length / 1024 : 0;
        let childPackageTotalSize = 0;
        const childPackageNum = activity.childPackages ? activity.childPackages.length : 0;
        const childPackageSizes = activity.childPackages ? activity.childPackages.map((pkg) => {
            if (!pkg.packageContent) {
                return 0;
            }
            if (isExpressionObject(pkg.packageContent)) {
                return 'expression';
            }
            const size = Buffer.from(pkg.packageContent).length / 1024;
            childPackageTotalSize += size;
            return size;
        }).join(', ') : '';
        this._logEvent(new SsisInlinePackageEvent(ExecuteSSISPackageActivityAdapter.componentName, inlinePackageEventType.ToPayload,
            `Main package size: ${mainPackageSize}KB. Child package number: ${childPackageNum}. Child package total size: ${childPackageTotalSize}. Child package size: ${childPackageSizes}`));
        return {
            type: ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE,
            typeProperties: locTypeProperties
        };
    }

    private getConfigurationAccessCredential(activity: ExecuteSSISPackageActivity): Model.SSISAccessCredential {
        if (!activity.useConfigAccessCredentials) {
            return undefined;
        }
        const accessDomain = ValueAdapter.toResource(activity,
            activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_DOMAIN : ExecuteSSISPackageActivity.CONFIG_ACCESS_DOMAIN);
        const accessUserName = ValueAdapter.toResource(activity,
            activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_USERNAME : ExecuteSSISPackageActivity.CONFIG_ACCESS_USERNAME);
        const accessPassword = ValueAdapter.toResource(activity,
            activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_PASSWORD : ExecuteSSISPackageActivity.CONFIG_ACCESS_PASSWORD);
        const configAccessCredential: Model.SSISAccessCredential = {
            domain: (accessDomain && accessDomain instanceof AzureKeyVault) ? akvToResource(accessDomain, this.context) : !stringIsNullOrEmpty(accessDomain) ? accessDomain : undefined,
            userName: (accessUserName && accessUserName instanceof AzureKeyVault) ? akvToResource(accessUserName, this.context) : !stringIsNullOrEmpty(accessUserName) ? accessUserName : undefined,
            password: accessPassword ? (isExpressionObject(accessPassword) ? accessPassword : passwordOrAKVtoSecretBase(accessPassword, this.context)) : undefined
        };
        if (!configAccessCredential.domain && !configAccessCredential.userName && !configAccessCredential.password) {
            return undefined;
        }
        return configAccessCredential;
    }

    private getSqlServerPackageLocation(activity: ExecuteSSISPackageActivity, path: string): Model.SSISPackageLocation {
        const sqlServerLinkedService = activity.sqlServerLinkedService ? linkedServiceReferenceToResource(activity.sqlServerLinkedService, this.context,
            this.ensureLogger()) : undefined;
        const configPath = activity && !stringIsNullOrEmpty(activity.configFile) ? ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.CONFIGURATION_FILE) : undefined;
        const configAccessCredential = this.getConfigurationAccessCredential(activity);
        const packagePassword = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_PASSWORD);
        const sqlServerTypeProperties: Model.SSISPackageLocationTypeProperties = {
            packagePassword: packagePassword ? (isExpressionObject(packagePassword) ? packagePassword : passwordOrAKVtoSecretBase(packagePassword, this.context)) : undefined,
            configurationPath: configPath,
            configurationAccessCredential: configPath ? configAccessCredential : undefined,
            sqlLinkedService: sqlServerLinkedService
        };
        return {
            type: ExecuteSSISPackageActivity.PACKAGE_LOCATION_SQL_SERVER,
            packagePath: path,
            typeProperties: sqlServerTypeProperties
        };
    }

    private getPackageStorePackageLocation(activity: ExecuteSSISPackageActivity, path: string): Model.SSISPackageLocation {
        const configPath = activity && !stringIsNullOrEmpty(activity.configFile) ? ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.CONFIGURATION_FILE) : undefined;
        const configAccessCredential = this.getConfigurationAccessCredential(activity);
        const packagePassword = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.PACKAGE_PASSWORD);
        const packageStoreTypeProperties: Model.SSISPackageLocationTypeProperties = {
            packagePassword: packagePassword ? (isExpressionObject(packagePassword) ? packagePassword : passwordOrAKVtoSecretBase(packagePassword, this.context)) : undefined,
            configurationPath: configPath,
            configurationAccessCredential: configAccessCredential
        };
        return {
            type: ExecuteSSISPackageActivity.PACKAGE_LOCATION_PACKAGE_STORE,
            packagePath: path,
            typeProperties: packageStoreTypeProperties
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getLogLocation(activity: ExecuteSSISPackageActivity): any {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let accessDomain: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let accessUserName: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let accessPassword: any;
        if (activity.packageLocation === ExecuteSSISPackageActivity.PACKAGE_LOCATION_INLINE_PACKAGE) {
            accessDomain = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.LOG_ACCESS_DOMAIN);
            accessUserName = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.LOG_ACCESS_USERNAME);
            accessPassword = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.LOG_ACCESS_PASSWORD);
        } else {
            accessDomain = ValueAdapter.toResource(activity,
                activity.copyCredentialForLog ?
                    (activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_DOMAIN : ExecuteSSISPackageActivity.PACKAGE_ACCESS_DOMAIN)
                    : ExecuteSSISPackageActivity.LOG_ACCESS_DOMAIN);
            accessUserName = ValueAdapter.toResource(activity,
                activity.copyCredentialForLog ?
                    (activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_USERNAME : ExecuteSSISPackageActivity.PACKAGE_ACCESS_USERNAME)
                    : ExecuteSSISPackageActivity.LOG_ACCESS_USERNAME);
            accessPassword = ValueAdapter.toResource(activity,
                activity.copyCredentialForLog ?
                    (activity.copyCredentialForPackage ? ExecuteSSISPackageActivity.WINAUTH_PASSWORD : ExecuteSSISPackageActivity.PACKAGE_ACCESS_PASSWORD)
                    : ExecuteSSISPackageActivity.LOG_ACCESS_PASSWORD);
        }
        const logAccessCredential: Model.SSISAccessCredential = {
            domain: (accessDomain && accessDomain instanceof AzureKeyVault) ? akvToResource(accessDomain, this.context) : accessDomain,
            userName: (accessUserName && accessUserName instanceof AzureKeyVault) ? akvToResource(accessUserName, this.context) : accessUserName,
            password: accessPassword ? (isExpressionObject(accessPassword) ? accessPassword : passwordOrAKVtoSecretBase(accessPassword, this.context)) : undefined
        };
        const logTypeProperties: Model.SSISLogLocationTypeProperties = {
            accessCredential: logAccessCredential
        };
        const logLocation: Model.SSISLogLocation = {
            logPath: ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.LOG_PATH),
            type: 'File',
            typeProperties: logTypeProperties
        };
        return logLocation;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private setLoggingLevel(activity: ExecuteSSISPackageActivity, loggingLevel: any): void {
        if (!loggingLevel) {
            return;
        }
        if (loggingLevel['type'] === 'Expression') {
            ExecuteSSISPackageActivity.LOGGING_LEVEL.setTemplateValue(activity, loggingLevel.value);
        } else {
            const value = loggingLevel as string;
            activity.useCustomizedLoggingLevel = value.toUpperCase().startsWith(ExecuteSSISPackageActivity.CUSTOMIZED_LOGGING_LEVEL_PREFIX);
            if (activity.useCustomizedLoggingLevel) {
                ExecuteSSISPackageActivity.LOGGING_LEVEL.setValue(activity, value.substring(ExecuteSSISPackageActivity.CUSTOMIZED_LOGGING_LEVEL_PREFIX.length));
            } else {
                ExecuteSSISPackageActivity.LOGGING_LEVEL.setValue(activity, value);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getLoggingLevel(activity: ExecuteSSISPackageActivity): any {
        const loggingLevel = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.LOGGING_LEVEL);
        if (!loggingLevel) {
            return null;
        }
        if (loggingLevel['type'] === 'Expression') {
            return loggingLevel;
        }
        return activity.useCustomizedLoggingLevel ? ExecuteSSISPackageActivity.CUSTOMIZED_LOGGING_LEVEL_PREFIX + loggingLevel : loggingLevel;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private setExecutionCredential(activity: ExecuteSSISPackageActivity, executionCredential: any): void {
        if (!executionCredential) {
            activity.useWinauth = false;
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.WINAUTH_DOMAIN, '');
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.WINAUTH_USERNAME, '');
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.WINAUTH_PASSWORD, '');
        } else {
            activity.useWinauth = true;
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.WINAUTH_DOMAIN, isAKVObject(executionCredential.domain) ? akvToModel(executionCredential.domain, this.context) : executionCredential.domain);
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.WINAUTH_USERNAME,
                isAKVObject(executionCredential.userName) ? akvToModel(executionCredential.userName, this.context) : executionCredential.userName);
            ValueAdapter.toModel(activity, ExecuteSSISPackageActivity.WINAUTH_PASSWORD,
                isExpressionObject(executionCredential.password) ? executionCredential.password.value : secretBaseToPasswordOrAKV(executionCredential.password, true, this.context));
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getExecutionCredential(activity: ExecuteSSISPackageActivity): any {
        const domain = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.WINAUTH_DOMAIN);
        const username = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.WINAUTH_USERNAME);
        const password = ValueAdapter.toResource(activity, ExecuteSSISPackageActivity.WINAUTH_PASSWORD);
        const executionCredential: Model.SSISExecutionCredential = {
            domain: (domain && domain instanceof AzureKeyVault) ? akvToResource(domain, this.context) : domain,
            userName: (username && username instanceof AzureKeyVault) ? akvToResource(username, this.context) : username,
            password: isExpressionObject(password) ? password : passwordOrAKVtoSecretBase(password, this.context)
        };
        return executionCredential;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private toChildPackageObject(configs: ExecuteSSISPackageChildPackageConfig[]): Model.SSISChildPackage[] {
        if (!configs) {
            return undefined;
        }
        return configs.map((config) => {
            const packagePath = ValueAdapter.toResource(config, ExecuteSSISPackageChildPackageConfig.PACKAGE_PATH);
            const inlinePackageName = ValueAdapter.toResource(config, ExecuteSSISPackageChildPackageConfig.INLINE_PACKAGE_NAME);
            const packageContent = ValueAdapter.toResource(config, ExecuteSSISPackageChildPackageConfig.PACKAGE_CONTENT);
            const packageLastModifiedDate = ValueAdapter.toResource(config, ExecuteSSISPackageChildPackageConfig.PACKAGE_LAST_MODIFIED_DATE);
            return { packagePath, inlinePackageName, packageContent, packageLastModifiedDate };
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private toChildPackageClass(pObjects: any[], owner: Owned): ExecuteSSISPackageChildPackageConfig[] {
        if (!pObjects) {
            return [];
        }
        return pObjects.map((pObject) => {
            const config = Owned.create<ExecuteSSISPackageChildPackageConfig>(ExecuteSSISPackageChildPackageConfig.MODEL, owner);
            ValueAdapter.toModel(config, ExecuteSSISPackageChildPackageConfig.PACKAGE_PATH, pObject.packagePath);
            ValueAdapter.toModel(config, ExecuteSSISPackageChildPackageConfig.INLINE_PACKAGE_NAME, pObject.inlinePackageName);
            ValueAdapter.toModel(config, ExecuteSSISPackageChildPackageConfig.PACKAGE_CONTENT, pObject.packageContent);
            ValueAdapter.toModel(config, ExecuteSSISPackageChildPackageConfig.PACKAGE_LAST_MODIFIED_DATE, pObject.packageLastModifiedDate);
            return config;
        });
    }
}
